<script setup>
import { computed } from 'vue'
import { router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import UiSelect from '@/Components/UI/UiSelect.vue'
import Icon from '@/Components/UI/Icon.vue'

defineOptions({ layout: AppLayout })
const { t } = useI18n()

const props = defineProps({
  month:    { type: String, default: '' },        // "YYYY-MM"
  range:    { type: Object, default: () => ({}) }, // grid_start/grid_end/month_start/month_end (iso)
  filters:  { type: Object, default: () => ({}) },
  bookings: { type: Array, default: () => [] },
  tours:    { type: Array, default: () => [] },
  vendors:  { type: Array, default: () => [] },
})

// Local-date helpers — never use toISOString().slice (UTC) for "today"/nav.
// Nairobi is UTC+3; UTC slicing misfires late in the day. (CLAUDE.md gotcha.)
function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function parseISO(s) { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d) }

const gridStart  = computed(() => parseISO(props.range.grid_start))
const gridEnd    = computed(() => parseISO(props.range.grid_end))
const monthStart = computed(() => parseISO(props.range.month_start))

// Build the day cells from grid_start..grid_end inclusive.
const days = computed(() => {
  const out = []
  const cur = new Date(gridStart.value)
  while (cur <= gridEnd.value) {
    out.push(new Date(cur))
    cur.setDate(cur.getDate() + 1)
  }
  return out
})

const weekdayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function chipsFor(day) {
  const key = ymd(day)
  return props.bookings.filter(b => b.starts_on <= key && b.ends_on >= key)
}
function isFirstDay(b, day) { return b.starts_on === ymd(day) }
function inCurrentMonth(day) { return day.getMonth() === monthStart.value.getMonth() }

function navMonth(delta) {
  const d = new Date(monthStart.value)
  d.setMonth(d.getMonth() + delta)
  go(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`)
}
function goToday() {
  const n = new Date()
  go(`${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`)
}
function go(month) {
  router.get('/tour_calendar', { ...props.filters, month }, { preserveState: true, replace: true })
}
function setFilter(key, value) {
  router.get('/tour_calendar', { ...props.filters, month: props.month, [key]: value || null },
    { preserveState: true, replace: true })
}

const monthLabel = computed(() =>
  monthStart.value.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }))

const tourOptions = computed(() =>
  [{ value: '', label: t('tourCalendar.allTours') }, ...props.tours.map(x => ({ value: x.id, label: x.title }))])
const vendorOptions = computed(() =>
  [{ value: '', label: t('tourCalendar.allVendors') }, ...props.vendors.map(x => ({ value: x.id, label: x.name }))])
</script>

<template>
  <PageShell :title="t('tourCalendar.title')">
    <template #actions>
      <div class="flex items-center gap-2">
        <UiButton
          variant="ghost"
          size="sm"
          @click="navMonth(-1)"
        >
          <Icon
            name="chev-l"
            :size="16"
          />
        </UiButton>
        <UiButton
          variant="secondary"
          size="sm"
          @click="goToday"
        >
          {{ t('tourCalendar.today') }}
        </UiButton>
        <UiButton
          variant="ghost"
          size="sm"
          @click="navMonth(1)"
        >
          <Icon
            name="chev-r"
            :size="16"
          />
        </UiButton>
        <span class="ml-2 fw-value">{{ monthLabel }}</span>
      </div>
    </template>

    <template #filters>
      <UiSelect
        :model-value="filters.tour_id || ''"
        :options="tourOptions"
        @update:model-value="v => setFilter('tour_id', v)"
      />
      <UiSelect
        :model-value="filters.vendor_id || ''"
        :options="vendorOptions"
        @update:model-value="v => setFilter('vendor_id', v)"
      />
      <a
        href="/tour_bookings/new"
        class="xl:ml-auto inline-flex items-center"
      >
        <UiButton
          variant="primary"
          size="sm"
        >{{ t('tourCalendar.newBooking') }}</UiButton>
      </a>
    </template>

    <!-- Weekday header -->
    <div class="grid grid-cols-7 gap-px text-xs font-semibold uppercase tracking-wider text-[color:var(--muted)] mb-1">
      <div
        v-for="w in weekdayLabels"
        :key="w"
        class="px-2 py-1"
      >
        {{ w }}
      </div>
    </div>

    <!-- Day grid -->
    <div class="grid grid-cols-7 gap-px bg-[color:var(--line)] rounded-[var(--radius-lg)] overflow-hidden">
      <div
        v-for="day in days"
        :key="ymd(day)"
        class="min-h-24 bg-[color:var(--surface)] p-1.5"
        :class="!inCurrentMonth(day) ? 'opacity-45' : ''"
      >
        <div class="text-xs text-[color:var(--muted)] mb-1">
          {{ day.getDate() }}
        </div>
        <div class="flex flex-col gap-1">
          <a
            v-for="b in chipsFor(day)"
            :key="b.id + '-' + ymd(day)"
            :href="b.href"
            class="block truncate rounded px-1.5 py-0.5 text-xs text-[color:var(--ink)]"
            :style="{ background: `color-mix(in oklch, var(${b.color_token}) 22%, var(--surface))`,
                      borderLeft: `3px solid var(${b.color_token})` }"
            :title="`${b.tour_title} · ${b.customer_name} · ${b.pax} pax`"
          >
            <span v-if="isFirstDay(b, day)">{{ b.tour_title }}</span>
            <span
              v-else
              class="opacity-60"
            >…</span>
          </a>
        </div>
      </div>
    </div>
  </PageShell>
</template>
